import { LoadingSpinner } from "@app/form_components";
import React from "react";
import ReactDOM from "react-dom";
import { defaultServiceContext } from "@app/contexts";
import {
  MergeChangesSummary,
  MergeDialogControls,
  SaveCooperativeYieldCheckbox
} from "@app/page-components";
import {
  BehaviorSubject,
  DesignBranchingEventType,
  DesignMap,
  LoadingProgressStatusPhase
} from "@app/types";
import {
  addBranchingEvent,
  applyChangeMap,
  getTypedEntries
} from "@app/utilities";
import Utility from "@app/utilities/Utility";
import BranchMergeEventFactory from "@app/utilities/versioning/BranchMergeEventFactory";
import { createDesignChangeMap } from "@app/utilities/versioning";
import { getChangeMapsConflicts } from "@app/utilities/versioning/conflicts/functions/get-change-maps-conflicts";
import ChangesContainer from "../components/versioning/change-log/ChangesContainer";
import { MergeConflictDialog } from "./MergeConflictDialog";
import { MergeDialogType } from "./MergeDialogOpenProps";

export {
  MergeDialogType
};

/**
 * JSDoc imports.
 *
 * @import { DesignChangeMap, LoadingProgressStatus } from '@app/types';
 * @import { MergeDialogOpenProps } from './MergeDialogOpenProps';
 */

/**
 * @typedef {Object} MergeDialogProps
 * @property [string] newVersionNumber
 * @property [(summary: string) => void] onSummaryGenerated
 * @property [boolean] saveAsNewVersion
 * @property [(newMergeEnabled: boolean) => void] setMergeEnabled
 * @property {DesignChangeMap} sourceChangeMap
 * @property {Design} sourceDesign
 * @property [Design] sourceOriginDesign
 * @property {DesignChangeMap} targetChangeMap
 * @property {Design} targetDesign
 * @property {MergeDialogType} type
 */

/**
 * @typedef {Object} MergeDialogState
 * @property {DesignChangeMap} currChangeMap
 * @property {DesignChangeMap} prevChangeMap
 * @property {boolean} changeMapsLoaded
 * @property {boolean} hasPreviousChanges
 * @property {boolean} hasCurrentChanges
 * @property {boolean} hidePositionChanges
 * @property {Set<string>} hiddenNames
 */

const {
  avsService,
  loadingService,
  workflowService
} = defaultServiceContext;

/**
 * UI for merging application versions with options for resolving conflicts 
 *
 * @class MergeDialog
 * @extends {React.Component<MergeDialogProps, MergeDialogState>}
 */
export class MergeDialog extends React.Component {
  _branchMergeEventFactory = new BranchMergeEventFactory();

  /**
   * Dialog for used in reference check
   * @module MergeDialog
   * @param {MergeDialogProps} props React props
   */
  constructor(props) {
    super(props);

    /** @type {MergeDialogState} */
    this.state = {
      currChangeMap: {},
      prevChangeMap: {},
      changeMapsLoaded: false,
      hasPreviousChanges: false,
      hasCurrentChanges: false,
      hidePositionChanges: true,
      hiddenNames: new Set()
    };
  }

  /**
   * Opens the merge dialog and displays it.
   *
   * @param {MergeDialogOpenProps}
   */
  static async open({
    defaultCommitMessage = '',
    isHotfix = false,
    newVersionNumber = null,
    onComplete = () => { },
    sourceDesign,
    sourceOriginDesign,
    targetDesign,
    targetOriginDesign,
    type = MergeDialogType.save
  }) {
    const languages = await workflowService.getLanguages();
    const showApplication = sourceDesign.application !== targetDesign.application;
    const sourceLabel = (showApplication) ? (
      `${sourceDesign.application} (${sourceDesign.version})`
    ) : sourceDesign.version;
    const targetLabel = (showApplication) ? (
      `${targetDesign.application} (${targetDesign.version})`
    ) : targetDesign.version;

    // Create change maps.
    let sourceChangeMap = createDesignChangeMap(
      sourceDesign,
      sourceOriginDesign
    );
    const targetChangeMap = createDesignChangeMap(
      targetDesign,
      targetOriginDesign
    );
    const conflictSections = getChangeMapsConflicts({
      languages,
      source: sourceChangeMap,
      sourceDesignMap: new DesignMap(sourceDesign),
      target: targetChangeMap,
      targetDesignMap: new DesignMap(targetDesign)
    });

    if (conflictSections.length) {
      sourceChangeMap = await MergeConflictDialog.open({
        conflictSections,
        sourceChangeMap,
        sourceLabel,
        targetChangeMap,
        targetLabel
      });

      if (sourceChangeMap == null) {
        // User cancelled the merge.
        onComplete();
        return;
      }
    }

    const dialogOpenLatestVersion = targetDesign.version;
    const saveAsNewVersion = !!newVersionNumber;
    newVersionNumber ??= sourceDesign.repoVersion;
    const dialogId = 'merge-dialog';
    const commitMessageId = `${dialogId}-commit-message`;
    const dialogTitle = [
      (type === MergeDialogType.pull) ? 'Pulling' : 'Merging',
      sourceLabel,
      'into',
      targetLabel
    ].join(' ');
    const buttonsSelector = '#w2ui-popup .w2ui-popup-buttons';

    window.apm.popup(
      [
        `<div>`,
        `   <span style="padding-right: 1em">${dialogTitle}</span>`,
        `</div>`
      ].join(''),
      `<div id="${dialogId}"></div>`,
      [
        `<div class="commit-message" id="${commitMessageId}"></div>`,
        `<button class="form-button normal cancel-button"> ${window.l.popup.cancel} </button>`,
        `<button class="form-button primary save-button" disabled> Save </button>`
      ].join(''),
      [
        `#${dialogId}`,
        () => {
          // Dialog elements.
          const cancelButton = document.querySelector(`${buttonsSelector} .cancel-button`);
          const commitMessageContainer = document.getElementById(commitMessageId);
          const dialogContainer = document.getElementById(dialogId);
          const saveButton = document.querySelector(`${buttonsSelector} .save-button`);

          // State variables.
          let commitMessage = defaultCommitMessage;
          let mergeEnabled = false;
          const commitMessageTextareaRef = React.createRef();

          const refreshSaveButtonState = () => {
            const isStateValid = (
              mergeEnabled
              && commitMessage.length >= 10
            );

            saveButton.disabled = !isStateValid;
          };

          // Append text (e.g. an AI-generated summary) to the commit-message
          // textarea, preserving any existing content with a blank-line break.
          const appendToCommitMessage = (text) => {
            const textarea = commitMessageTextareaRef.current;
            if (!textarea || !text) {
              return;
            }
            const current = textarea.value;
            const separator = (current.trim().length > 0) ? '\n\n' : '';
            const next = `${current}${separator}${text}`;
            textarea.value = next;
            commitMessage = next;
            refreshSaveButtonState();
          };

          const closeDialog = () => {
            ReactDOM.unmountComponentAtNode(dialogContainer);
            ReactDOM.unmountComponentAtNode(commitMessageContainer);
            w2popup.close();
          };

          const mergeDialog = ReactDOM.render(
            <MergeDialog
              newVersionNumber={newVersionNumber}
              onSummaryGenerated={appendToCommitMessage}
              saveAsNewVersion={saveAsNewVersion}
              setMergeEnabled={(enabled) => {
                mergeEnabled = enabled;
                refreshSaveButtonState();
              }}
              sourceChangeMap={sourceChangeMap}
              sourceDesign={sourceDesign}
              sourceOriginDesign={sourceOriginDesign}
              targetChangeMap={targetChangeMap}
              targetDesign={targetDesign}
              type={type}
            />,
            dialogContainer
          );

          ReactDOM.render(
            <>
              <p><span>*</span>Commit message is required for saving (at least 10 characters long)</p>
              <textarea
                defaultValue={commitMessage}
                onChange={(e) => {
                  commitMessage = e.target.value;
                  refreshSaveButtonState();
                }}
                placeholder="Enter a message about your changes"
                ref={commitMessageTextareaRef}
              />
              <SaveCooperativeYieldCheckbox />
            </>,
            commitMessageContainer
          );

          cancelButton.addEventListener('click', () => {
            closeDialog();
            onComplete();
          });

          saveButton.addEventListener('click', async () => {
            saveButton.disabled = true;
            closeDialog();

            // CORE-34511 - Cancel save if Merge Dialog is opened before a different version save to avoid overwrite
            // Skip this check when merging to a non-latest version (we'll use current version + 1 instead)
            if (!isHotfix) {
              const { branchId } = targetDesign;
              const latestVersion = (await loadingService.useLoading(
                  () => avsService.getLatestVersion(branchId),
                  'Checking latest version'
              ))?.version;
              const isStale = latestVersion !== dialogOpenLatestVersion;

              if (isStale) {
                window.apm.notification.post("error", "Latest version changed since Save was initiated. Click Save to re-compare your changes to the current latest version.", false);
                onComplete();
                return;
              }
            }

            /** @type {BehaviorSubject<LoadingProgressStatus>} */
            const progressMonitor = new BehaviorSubject({
              phase: LoadingProgressStatusPhase.preparing
            });
            await loadingService.useLoading(
              async () => {
                const mergedVersion = mergeDialog.getMergedVersion();

                await window.colm.saveWithCollaboration({
                  changeLogMap: mergeDialog.getCurrentVersionChangeLogMaps(),
                  commitMessage,
                  isHotfix,
                  mergedVersion,
                  newVersionNumber,
                  progressMonitor,
                  saveAsNewVersion
                });
              },
              {
                message: 'Saving to Repository...',
                progress: {
                  label: 'Design',
                  startedAt: Date.now(),
                  subject: progressMonitor
                }
              }
            );

            onComplete();
          });

          // Adjust the w2ui-box bottom to account for the commit-message + buttons height.
          // Fallback mirrors the default w2ui popup footer height when measurement fails
          // (e.g. buttons not yet laid out).
          const defaultW2uiButtonsHeight = 70;
          const popupButtonsHeight = $("div.w2ui-popup-buttons").outerHeight() || defaultW2uiButtonsHeight;
          $(".w2ui-popup .w2ui-box").css("bottom", `${popupButtonsHeight}px`);
        }
      ],
      700, // height
      null, // node
      null, // treenode
      false // showClose
    );
  }

  componentDidMount() {
    this.initChangeMaps();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.sourceChangeMap !== this.props.sourceChangeMap
      || nextProps.targetChangeMap !== this.props.targetChangeMap
    ) {
      this.initChangeMaps();
    }
  }

  /** Initialize merge by getting version changeMaps and identifying conflicts. */
  initChangeMaps() {
    const {
      sourceChangeMap,
      targetChangeMap
    } = this.props;

    this.setState({ currChangeMap: sourceChangeMap });
    this.setState({ hasCurrentChanges: Object.keys(sourceChangeMap).length > 0 });
    this.setState({ prevChangeMap: targetChangeMap });
    this.setState({ hasPreviousChanges: Object.keys(targetChangeMap).length > 0 });

    this.setState({ changeMapsLoaded: true });
    // callback to SaveWidget: enables/disabled Merge Button in popup UI
    this.props.setMergeEnabled?.(true);
  }

  /**
   * Get changelog map of two symetrical objects: previousChangesMap and savedChangesMap
   *
   * @returns {object} changelogMap
   */
  getCurrentVersionChangeLogMaps() {
    return [this.state.currChangeMap, this.state.prevChangeMap];
  }

  /**
   * Called externally by SaveWidget
   *
   * @returns {object} Merged application
   */
  getMergedVersion() {
    const {
      sourceDesign,
      sourceOriginDesign,
      targetDesign,
      type
    } = this.props;
    if (targetDesign) {
      let newVersion = _.cloneDeep(targetDesign);
      // Source is local when saving, but on merge/pull your local design is the target.
      const localDesign = (type === MergeDialogType.save) ? (
        sourceDesign
      ) : targetDesign;
      // newVersion requires $loki data for localStorage syncronization
      newVersion = Utility.setLokiAndMetaOnDesignVersion({
        targetVersion: newVersion,
        sourceVersion: localDesign
      });
      newVersion.author = window.apm.getDesignerAuthor();
      const timestamp = window.apm.timestamp;
      const {
        _id,
        application,
        version
      } = sourceDesign;

      switch (type) {
        case MergeDialogType.merge: {
          const mergeEvent = this._branchMergeEventFactory.getEvent(
            DesignBranchingEventType.merge,
            {
              application,
              author: newVersion.author,
              branchId: _id,
              branchVersion: version,
              timestamp
            }
          );
          addBranchingEvent(newVersion, mergeEvent);
          break;
        }
        case MergeDialogType.pull:
          const mergeEvent = this._branchMergeEventFactory.getEvent(
            DesignBranchingEventType.pull,
            {
              author: newVersion.author,
              parentApplication: application,
              timestamp,
              version
            }
          );
          addBranchingEvent(newVersion, mergeEvent);
          break;
        case MergeDialogType.save:
          // Get the latest timestamp from an array of branching events.
          const getLastBranchingTimestamp = (branching) => {
            return (branching ?? []).reduce((lastTimestamp, event) => {
              return Math.max(lastTimestamp, event.timestamp);
            }, 0);
          }
          // Get the latest timestamp from a map of branching events.
          const getLastBranchingMapTimestamp = (branchingMap) => {
            return Object.values(branchingMap ?? {}).reduce((lastTimestamp, events) => {
              return Math.max(lastTimestamp, getLastBranchingTimestamp(events));
            }, 0);
          }
          if ('branching' in sourceDesign) {
            // Take whichever one is more up to date.
            const takeSource = (
              getLastBranchingTimestamp(sourceDesign.branching)
              > getLastBranchingTimestamp(newVersion.branching)
            );
            newVersion.branching = (takeSource) ? (
              _.cloneDeep(sourceDesign.branching)
            ) : newVersion.branching;
          }

          if ('branchingMap' in sourceDesign) {
            // Take whichever one is more up to date.
            const takeSource = (
              getLastBranchingMapTimestamp(sourceDesign.branchingMap)
              > getLastBranchingMapTimestamp(newVersion.branchingMap)
            );
            newVersion.branchingMap = (takeSource) ? (
              _.cloneDeep(sourceDesign.branchingMap)
            ) : newVersion.branchingMap;
          }

          if ('branchingMerging' in sourceDesign) {
            // Take whichever one is more up to date.
            const takeSource = (
              sourceDesign.branchingMerging.lastLocalChange
              > (newVersion.branchingMerging?.lastLocalChange ?? 0)
            );
            newVersion.branchingMerging = (takeSource) ? (
              _.cloneDeep(sourceDesign.branchingMerging)
            ) : newVersion.branchingMerging;
          }
          break;
      }

      return applyChangeMap({
        changeMap: this.state.currChangeMap,
        design: newVersion
      });
    }

    const newVersion = { ...sourceDesign };
    newVersion.version = (sourceOriginDesign ?? sourceDesign).version;
    newVersion.repoVersion = (sourceOriginDesign ?? sourceDesign).version;
    newVersion.author = window.apm.getDesignerAuthor();

    // TODO: See CORE-30634
    // do not merge latest intentHandling, last save always wins
    // TODO: should be using changeMaps. Update changeMaps to have this data

    return applyChangeMap({
      changeMap: this.state.prevChangeMap,
      design: newVersion
    });
  }

  getFilterProps() {
    const { hiddenNames, hidePositionChanges } = this.state;
    return {
      hidePositionChanges,
      hiddenNames,
      onHideName: (name) => {
        this.setState({ hiddenNames: new Set([...hiddenNames, name]) });
      }
    };
  }

  getCurrentChangesHeader() {
    const title = "Your Changes";
    const version = (this.props.saveAsNewVersion) ? this.props.newVersionNumber : window.apm.serialized.repoVersion;
    const author = window.apm.getDesignerAuthor();
    return (
      <h3>
        <span className="labels">
          <i className={"fe icon-file-text"}></i>
          <span>{title}</span>
          <i className={"fe icon-hash"}></i>
          <span>{version}</span>
          <i className={"fe icon-user"}></i>
          <span>{author}</span>
        </span>
      </h3>
    );
  }

  getEmptyMessage() {
    return (
      <div className="panel">
        <h5 className="emptyMessage">No changes in this version</h5>
      </div>
    );
  }

  /**
   * @param {'current' | 'previous'} current
   * @param {keyof DesignChangeMap} key
   */
  returnSortedChanges(current, key) {
    const changeMap = current === "current" ? this.state.currChangeMap : this.state.prevChangeMap;

    /**
     * @param {string | undefined} a
     * @param {string | undefined} b
     */
    const compareStrings = (a, b) => {
      return (a ?? '').localeCompare(b ?? '');
    }

    switch (key) {
      case "ccxml":
      case "modules":
        return getTypedEntries(changeMap[key]);
      case "application-intents":
      case "application-modules":
      case "application-objects":
      case "application-variables":
        return (
          getTypedEntries(changeMap[key])
          .sort(([, changeA], [, changeB]) => {
            const { _class: classA, name: nameA } = changeA.node;
            const { _class: classB, name: nameB } = changeB.node;

            return (classA !== classB) ? compareStrings(classA, classB) : (
              compareStrings(nameA, nameB)
            );
          })
        );
      case "appProperties":
        return getTypedEntries(changeMap[key]).sort(([, changeA], [, changeB]) => {
          return compareStrings(changeA.object.config, changeB.object.config);
        });
      case "compoundIntents":
        return getTypedEntries(changeMap[key]).sort(([, changeA], [, changeB]) => {
          return compareStrings(changeA.object.toIntent, changeB.object.toIntent);
        });
      case "csvData":
      case "folders":
      case "functions":
      case "graphQLSchemas":
      case "unitTests":
        return getTypedEntries(changeMap[key]).sort(([, changeA], [, changeB]) => {
          return compareStrings(changeA.object.name, changeB.object.name);
        });
      case "fillerPrompts":
        return getTypedEntries(changeMap[key]).sort(([keyA], [keyB]) => {
          return compareStrings(keyA, keyB);
        });
      case "globalIntents":
        return getTypedEntries(changeMap[key]).sort(([, changeA], [, changeB]) => {
          return compareStrings(
            changeA.object.moduleProperties.name,
            changeB.object.moduleProperties.name
          );
        });
      case "applicationVersion":
      case "branchingMap":
      case "globalErrorHandling":
      case "globalExceptionHandling":
      case "globalLoopHandling":
      case "intentHandling":
      case "languages":
      case "settings":
        return changeMap[key];
    }
  }
  returnContainers(current, key) {
    const getLabel = Utility.getChangeLogLabel(key);
    const sortedChanges = this.returnSortedChanges(current, key);
    const notMapped = ["globalErrorHandling", "globalExceptionHandling", "globalLoopHandling", "intentHandling", "branchingMap", "branching"];
    const simpleChanges = ["languages", "applicationVersion"]
    const filterProps = this.getFilterProps();

    if (key === "settings") {
      const changeMap = current === "current" ? this.state.currChangeMap : this.state.prevChangeMap;
      const clonedSettings = _.cloneDeep(changeMap['settings']);

      if (clonedSettings["intentHandling"]) {
        if (clonedSettings["intentHandling"].changeset) {
          clonedSettings["intentHandling"].changeset.forEach(cs => {
            const iKey = cs.key[0];
            clonedSettings[iKey] = clonedSettings["intentHandling"][iKey];
          });

          delete clonedSettings["intentHandling"];
        }
      }

      if (clonedSettings["globalLoopHandling"]) {
        Object.keys(clonedSettings["globalLoopHandling"]).forEach(iKey => {
          clonedSettings[iKey] = clonedSettings["globalLoopHandling"][iKey];
        });

        delete clonedSettings["globalLoopHandling"];
      }

      return (
        <div className="panel" key={`${current}-${key}`}>
          <h4>Settings</h4>
          {Object.keys(clonedSettings).sort((a, b) => (a ?? '').localeCompare(b ?? '')).map((settingKey) => {
            return (
              <ChangesContainer
                listType={settingKey}
                label={getLabel}
                changeMap={clonedSettings[settingKey]}
                serialized={this.props.sourceDesign}
                key={settingKey}
                {...filterProps}
              />
            );
          })}
        </div>
      );
    }
    if (!simpleChanges.includes(key) && notMapped.indexOf(key) === -1) {
      return (
        <div className="panel" key={`${current}-${key}`}>
          <h4>{getLabel}</h4>
          {sortedChanges.map(cm => {
            return (
              <ChangesContainer
                listType={key}
                changeMap={cm}
                serialized={this.props.sourceDesign}
                key={cm[0]}
                {...filterProps}
              />
            );
          })}
        </div>
      );
    }
    if (!simpleChanges.includes(key) && notMapped.indexOf(key) !== -1) {
      return (
        <div className="panel" key={`${current}-${key}`}>
          <h4>{getLabel}</h4>
          {(() => {
            if (this.props.type && this.props.type === "merge" && key === "branchingMap" ||
              this.props.type && this.props.type === "pull" && key === "branching") {
              return;
            }
            return (
              <ChangesContainer
                listType={key}
                changeMap={sortedChanges}
                key={key}
                {...filterProps}
              />
            );
          })()}
        </div>
      );
    }
    if (simpleChanges.includes(key)) {
      return (
        <div className="panel" key={`${current}-${key}`}>
          <h4>{getLabel}</h4>

          <ChangesContainer
            listType={key}
            changeMap={[key, sortedChanges]}
            key={key}
            {...filterProps}
          />
        </div>
      );
    }
  }

  getChangeMapKeys(changeMaps) {
    let changeMapKeys = Object.keys(changeMaps);

    if (this.props.type && this.props.type === "merge") {
      changeMapKeys = changeMapKeys.filter(key => key !== "branchingMap")
    }

    return changeMapKeys;
  }

  /**
   * React render
   * @returns {XML}
   */
  render() {
    const hasPreviousChanges = Object.keys(this.state.prevChangeMap).length !== 0;
    const reviewMode = this.props.type === MergeDialogType.review;
    const changeMapsLoaded = this.state.changeMapsLoaded;
    const filterProps = this.getFilterProps();

    const hasPreviousChangeMaps = window.apm.changeLogMaps;
    const shouldDisplayPreviousChanges = hasPreviousChanges && !this.props.saveAsNewVersion && hasPreviousChangeMaps;
    const currChangeMapKeys = (changeMapsLoaded && this.state.hasCurrentChanges) ? this.getChangeMapKeys(this.state.currChangeMap) : null;
    const prevChangeMapKeys = (changeMapsLoaded && shouldDisplayPreviousChanges) ? Object.keys(this.state.prevChangeMap) : null;

    return (
      <div key={"merge-dialog-container"} className={"merge-dialog"}>

        {!changeMapsLoaded &&
          <div style={{ margin: '30% auto', height: '100%' }}>
            <LoadingSpinner message={"Comparing versions..."} />
          </div>
        }

        <ul className="nav nav-tabs">
          {!reviewMode && <li className="nav-button active">
            <a href="#currChangesTab" data-toggle="tab">Your changes</a>
          </li>}

          <li className={`nav-button ${reviewMode ? "active" : ""}`}>
            <a href="#prevChangesTab" data-toggle="tab">Latest changes</a>
          </li>

        </ul>
        <div id="merge-dialog-controls-bar">
          <MergeDialogControls
            hidePositionChanges={this.state.hidePositionChanges}
            hiddenNames={this.state.hiddenNames}
            onClearHiddenNames={() => this.setState({ hiddenNames: new Set() })}
            onRemoveHiddenName={(name) => {
              const hiddenNames = new Set(this.state.hiddenNames);
              hiddenNames.delete(name);
              this.setState({ hiddenNames });
            }}
            onTogglePositionChanges={(newValue) => this.setState({ hidePositionChanges: newValue })}
          />
        </div>
        <div className="tab-content clearfix">
          <div className={`tab-pane ${reviewMode ? "active" : ""}`} id="prevChangesTab">
            {changeMapsLoaded && (
              <div className="changeLogPanel">
                {shouldDisplayPreviousChanges
                  ? prevChangeMapKeys.map((key) => {
                    return this.returnContainers('previous', key)
                  })
                  : this.getEmptyMessage()}
              </div>
            )}
            {changeMapsLoaded && this.state.hasCurrentChanges && reviewMode &&
              <div className="changeLogPanel">
                {this.state.prevChangeMap['unitTests'] &&
                  <div className="panel">
                    <h4>Unit Tests</h4>
                    {Object.entries(this.state.prevChangeMap['unitTests']).map(ut => {
                      return (
                        <ChangesContainer
                          listType="unitTests"
                          changeMap={ut}
                          key={ut[0]}
                          {...filterProps}
                        />
                      );
                    })}
                  </div>
                }
              </div>
            }

          </div>
          {(reviewMode) ? null : (
            <div className="tab-pane active" id="currChangesTab">
              {changeMapsLoaded && this.state.hasCurrentChanges &&
                <div className="changeLogPanel">

                  {currChangeMapKeys.map(key => {
                    return this.returnContainers("current", key);
                  })}

                </div>
              }
              {changeMapsLoaded && !this.state.hasCurrentChanges &&
                <div className="changeLogPanel">
                  {this.getEmptyMessage()}
                </div>
              }
              {
                changeMapsLoaded
                && (
                  this.props.type === MergeDialogType.merge
                  || this.props.type === MergeDialogType.pull
                )
                && (
                  <MergeChangesSummary
                    onSummaryGenerated={this.props.onSummaryGenerated}
                    sourceBranchId={this.props.sourceDesign.branchId}
                    sourceBranchVersion={this.props.sourceDesign.version}
                  />
                )
              }
            </div>
          )}
        </div>
      </div>
    );

  }
}
